import { useContext, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import DrugScreenBanner from '@/components/DrugScreenBanner';
import DrugScreenCard from '@/components/DrugScreenCard';
import PageHeader from '@/components/PageHeader';
import defaultTimeZone from '@/constants/defaultTimeZone';
import { drugScreensDefaultSearchParams } from '@/constants/drugScreen';
import { STICKY_CHROME_CLASS } from '@/constants/layout';
import AppContext from '@/contexts/appContext';
import DrugScreensFilterForm from '@/forms/DrugScreensFilterForm';
import useAPI from '@/hooks/useAPI';
import usePagination from '@/hooks/usePagination';
import PersonContext from '@/pages/Person/PersonContext';
import DrugScreenRepository from '@/repositories/DrugScreenRepository';
import type { DrugScreensParams } from '@/repositories/DrugScreenRepository/types';
import Container from '@/ui/components/Container';
import Heading from '@/ui/components/Heading';
import LoadingSkeleton from '@/ui/components/LoadingSkeleton';
import Paginator from '@/ui/components/Paginator';
import drugScreenCompliantResult from '@/utils/drugScreenCompliantResult';
import formatPersonName from '@/utils/formatPersonName';

import NewDrugScreenButton from './components/NewDrugScreenButton';

const PersonDrugScreens = () => {
  const { host } = useContext(AppContext);
  const { person } = useContext(PersonContext);
  const drugScreenRepository = DrugScreenRepository();

  const [searchParams, setSearchParams] = useSearchParams({
    ...drugScreensDefaultSearchParams,
    person: person.id
  });
  const [totalPages, setTotalPages] = useState<number>(0);

  const {
    episode_of_care: episodeOfCare,
    program_qualification: programQualification,
    targeted_substances: targetedSubstances
  } = person;

  const compliantResult = useMemo(() => {
    if (!episodeOfCare?.program.drug_category) {
      return 'N/A (No program)';
    }

    return drugScreenCompliantResult(episodeOfCare.program.drug_category);
  }, [episodeOfCare?.program.drug_category]);

  const { data: drugScreensCounts, isPending: isDrugScreensCountsPending } = useAPI({
    queryKey: ['getPersonDrugScreensCounts', person.id],
    queryFn: async () => {
      return await drugScreenRepository.getPersonDrugScreensCounts(person.id);
    }
  });

  const {
    data: drugScreensResponse,
    isError: isDrugScreensError,
    isPending: isDrugScreensPending,
    isSuccess: isDrugScreensSuccess
  } = useAPI({
    queryKey: ['getPersonDrugScreens', person.id, Object.fromEntries(searchParams)],
    queryFn: async () => {
      return await drugScreenRepository.getDrugScreens({
        ...(Object.fromEntries(searchParams) as DrugScreensParams),
        person: person.id
      });
    }
  });

  useEffect(() => {
    if (isDrugScreensSuccess) {
      if (drugScreensResponse?.data?.page) {
        setSearchParams(
          (currentParams) => {
            currentParams.set('page', drugScreensResponse.data?.page.toString() ?? '1');
            return currentParams;
          },
          { replace: true }
        );
      }

      if (drugScreensResponse?.data?.total_pages) {
        setTotalPages(drugScreensResponse.data.total_pages);
      }
    }
  }, [
    drugScreensResponse?.data?.page,
    drugScreensResponse?.data?.total_pages,
    isDrugScreensSuccess,
    setSearchParams
  ]);

  const { currentPage, goToPage, nextPage, prevPage } = usePagination(
    +(searchParams.get('page') ?? '1'),
    totalPages
  );

  // Replaces rather than pushes, as on the challenges page: a history entry for
  // the page the reader is already on is one the Back button has to be pressed
  // through before it leaves.
  useEffect(() => {
    setSearchParams(
      (currentParams) => {
        currentParams.set('page', currentPage.toString());
        return currentParams;
      },
      { replace: true }
    );
  }, [currentPage, setSearchParams]);

  return (
    <Container type="page">
      <div className={`mb-5 pt-1 z-19 bg-gray-100 ${STICKY_CHROME_CLASS.pageSection}`}>
        <PageHeader headingText="Drug Screens" className="sr-only" />
        <DrugScreensFilterForm activeParams={searchParams} setParams={setSearchParams} />

        <div className="mb-5 pb-2">
          <NewDrugScreenButton />
        </div>
      </div>

      {isDrugScreensCountsPending ? (
        <LoadingSkeleton spinnerProps={{ label: 'Loading metrics...' }} />
      ) : (
        programQualification?.primary_substance &&
        episodeOfCare &&
        episodeOfCare.status === 'active' &&
        drugScreensCounts?.data && (
          <DrugScreenBanner
            program={episodeOfCare.program.name}
            substances={targetedSubstances}
            compliantResult={compliantResult}
            drugScreenCounts={drugScreensCounts.data}
          />
        )
      )}
      <hr />
      {isDrugScreensPending ? (
        <LoadingSkeleton spinnerProps={{ label: 'Loading drug screens...' }} />
      ) : !isDrugScreensError && drugScreensResponse?.data?.results?.length ? (
        <ul className="space-y-4 my-2">
          {drugScreensResponse.data.results.map((drugScreen) => (
            <li key={drugScreen.id}>
              <DrugScreenCard
                drugScreen={drugScreen}
                reviewLink={`${host}/drug_screens/${drugScreen.id}`}
                personTimeZone={person.time_zone || defaultTimeZone}
              />
            </li>
          ))}
        </ul>
      ) : (
        <div className="text-center p-8">
          <Heading
            className="mp-sensitive"
            text={`No drug screens found for ${formatPersonName({ person })}`}
            value={3}
          />
        </div>
      )}
      <Paginator
        currentPage={currentPage}
        goToPage={goToPage}
        nextPage={nextPage}
        prevPage={prevPage}
        totalPages={totalPages}
      />
    </Container>
  );
};

export default PersonDrugScreens;
